/**
 * Romanisation, generated from the Uthmani text — the `generated` scheme (L29).
 *
 * ── Why generate one at all ─────────────────────────────────────────────────────
 *
 * The two off-the-shelf scheme families both come with an unresolved licence question,
 * and neither can be corrected when it is wrong. This one has no licence exposure, works
 * offline, and is the only one we can fix. It is also the only one that can read the
 * TAJWEED RUNS, and the runs turn out to carry most of what makes this hard:
 *
 *   `slnt`             → the letter is written but not pronounced. Nothing in the
 *                        diacritics says so; without this, ٱلَّذِينَ gains a syllable.
 *   `ham_wasl`         → this alif is the prosthetic one, voiced only utterance-initially.
 *   `laam_shamsiyah`   → this lām assimilates into the next letter. Guessing it from
 *                        "bare lām before a shadda" worked, but the data simply says so.
 *   `end`              → the ayah-number glyph, which is not part of the recitation.
 *
 * ── Two orthographic facts this corpus taught us, by measurement ────────────────
 *
 *  1. **U+0672 (alif with wavy hamza above) is used as a long-ā carrier**, not as a
 *     hamza. `ٱلصِّرَٲطَ` in 1:6 is `aṣ-ṣirāṭa`. Mapping it as a glottal stop — or, as the
 *     first draft did, not mapping it at all — silently shortens the vowel.
 *  2. **The name ٱللَّه writes no alif for its long ā.** `U+0671 0644 0644 0651 064E 0647`
 *     is `llāh`, and no per-letter rule can produce that `ā` because it is not there. It
 *     is special-cased, and it is the only word that is.
 *
 * ── What it does not model ──────────────────────────────────────────────────────
 *
 * The rules that live BETWEEN words: idgham across a word boundary, ikhfāʾ, iqlāb, the
 * ghunnah's two counts, and the pausal (waqf) form at the end of an ayah. Where the
 * script notates an inter-word idgham with a shadda the output shows the doubling
 * (`yakun llahū`) rather than the assimilation a reciter would actually produce.
 *
 * **So this is a word-recognition aid, not a recitation guide** — L29, and the UI says so
 * permanently. Paired with the audio, the ear corrects the eye. The ayah-level syllabic
 * dataset exists precisely because a per-letter scheme structurally cannot do better.
 *
 * Every Arabic codepoint below is an ESCAPE in a named constant, never a literal glyph in
 * a regex class (L24): a literal Arabic class bidi-reorders into one matching every
 * letter, silently.
 */
import type { TajweedRun } from './types'

// ---------------------------------------------------------------------------
// Two styles of the same romanisation
// ---------------------------------------------------------------------------

/**
 * Scholarly marks → plain ASCII letters.
 *
 * `bismi llāhi r-raḥmāni r-raḥīmi` → `bismi llahi r-rahmani r-rahimi`.
 *
 * The marked form is more precise: `ḥ` and `h` are genuinely different letters, and
 * collapsing them loses that. But a reader who has never seen the marks cannot act on
 * the distinction — they read `ḥ` as `h` regardless — and the dots and macrons make the
 * line LOOK like something they are not qualified to read, which is the opposite of the
 * point. So plain is the default and the marked form is one setting away.
 *
 * The hyphens survive: `r-rahmani` shows where the article assimilated, which is a
 * pronunciation cue rather than a scholarly notation.
 */
const PLAIN: Record<string, string> = {
  'ā': 'a',
  'ī': 'i',
  'ū': 'u',
  'ḥ': 'h',
  'ṣ': 's',
  'ḍ': 'd',
  'ṭ': 't',
  'ẓ': 'z',
}

/**
 * The two glottal marks, `ʿ` (ayn) and `ʾ` (hamza), become a plain apostrophe — but only
 * INSIDE a word.
 *
 * Dropping them outright was the first attempt and it silently merged syllables:
 * `naʿbudu` came out `nabudu`, which reads "na-bu-du" and is a different word. Mid-word
 * the mark is doing real work — it is the break between two vowels — so it has to leave
 * something behind.
 *
 * At the START of a word it is doing no such work: `ʿalayhim` and `ʾiyyāka` begin with a
 * vowel as far as an English reader is concerned, and a leading apostrophe there is
 * noise. Same after a hyphen, which is a word boundary the article left behind.
 */
const GLOTTAL = new Set(['ʿ', 'ʾ'])

export function simplifyMarks(text: string): string {
  let out = ''
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]!
    if (GLOTTAL.has(ch)) {
      const prev = out[out.length - 1]
      const wordStart = prev === undefined || prev === ' ' || prev === '-'
      if (!wordStart) out += "'"
      continue
    }
    out += ch in PLAIN ? PLAIN[ch]! : ch
  }
  return out.replace(/ {2,}/gu, ' ').trim()
}

// ---------------------------------------------------------------------------
// The inventory
// ---------------------------------------------------------------------------

const CONSONANTS: Record<string, string> = {
  'ء': 'ʾ', // hamza
  'آ': 'ʾā', // alif with madda above
  'أ': 'ʾ', // alif with hamza above
  'ؤ': 'ʾ', // waw with hamza above
  'إ': 'ʾ', // alif with hamza below
  'ئ': 'ʾ', // ya with hamza above
  'ا': '', // alif — a carrier, never a consonant
  'ب': 'b',
  'ة': 'h', // ta marbuta — pausal `h`; voiced to `t` when it carries a vowel
  'ت': 't',
  'ث': 'th',
  'ج': 'j',
  'ح': 'ḥ',
  'خ': 'kh',
  'د': 'd',
  'ذ': 'dh',
  'ر': 'r',
  'ز': 'z',
  'س': 's',
  'ش': 'sh',
  'ص': 'ṣ',
  'ض': 'ḍ',
  'ط': 'ṭ',
  'ظ': 'ẓ',
  'ع': 'ʿ',
  'غ': 'gh',
  'ف': 'f',
  'ق': 'q',
  'ك': 'k',
  'ل': 'l',
  'م': 'm',
  'ن': 'n',
  'ه': 'h',
  'و': 'w', // waw — consonant, or the carrier of ū
  'ى': '', // alif maqsura — a carrier
  'ي': 'y', // ya — consonant, or the carrier of ī
  'ٱ': '', // alif wasla — handled by `ham_wasl`
  'ٲ': '', // alif with wavy hamza above — a long-ā carrier in this corpus
}

const FATHA = 'َ'
const KASRA = 'ِ'
const DAMMA = 'ُ'
const FATHATAN = 'ً'
const KASRATAN = 'ٍ'
const DAMMATAN = 'ٌ'
const SHADDA = 'ّ'
const SUKUN = 'ْ'
const DAGGER_ALIF = 'ٰ' // superscript alef — a written-out long ā
const SMALL_WAW = 'ۥ' // a long ū with no full wāw
const SMALL_YA = 'ۦ' // a long ī with no full yāʾ
const TATWEEL = 'ـ'

const ALIF = 'ا'
const ALIF_MAQSURA = 'ى'
const ALIF_WASLA = 'ٱ'
const WAVY_ALIF = 'ٲ'
const WAW = 'و'
const YA = 'ي'
const LAM = 'ل'
const HA = 'ه'
const TA_MARBUTA = 'ة'

const SHORT_VOWELS: Record<string, string> = { [FATHA]: 'a', [KASRA]: 'i', [DAMMA]: 'u' }
const TANWIN: Record<string, string> = { [FATHATAN]: 'an', [KASRATAN]: 'in', [DAMMATAN]: 'un' }
const TANWIN_OUT = new Set(Object.values(TANWIN))

/** Which long vowel a written carrier produces, given the short vowel before it. */
const LENGTHEN: Record<string, string> = { a: 'ā', i: 'ī', u: 'ū' }

/** Carriers that always mean "lengthen what came before", whatever that was. */
const LONG_CARRIERS = new Set([ALIF, ALIF_MAQSURA, WAVY_ALIF, DAGGER_ALIF])

/**
 * Combining marks. U+064B–U+0652 are the vowels and shadda/sukun; U+0670 is the dagger
 * alif; U+06D6–U+06ED are the waqf signs, small high letters and the sajdah mark; U+0640
 * is the typographic tatweel.
 */
const MARKS = new Set([
  ...'ًٌٍَُِّْٰٕٓٔ',
  ...'ۖۗۘۙۚۛۜ۝۞۟۠ۡۢ',
  ...'ۣۤۥۦۧۨ۩۪ۭ۫۬',
  TATWEEL,
])

const isMark = (ch: string) => MARKS.has(ch)
/** Arabic-Indic digits U+0660–U+0669 — the ayah-number glyph. */
const isDigit = (ch: string) => ch >= '٠' && ch <= '٩'

// ---------------------------------------------------------------------------
// The walk
// ---------------------------------------------------------------------------

interface Cell {
  ch: string
  /** The tajweed rule ids the corpus attached to this character's run. */
  rules: readonly string[]
}

/** Flatten the runs into characters, each carrying its run's rule ids. */
function cellsFromRuns(runs: readonly TajweedRun[]): Cell[] {
  const cells: Cell[] = []
  for (const run of runs) {
    const rules = run.slice(1)
    if (rules.includes('end')) continue
    for (const ch of run[0]) cells.push({ ch, rules })
  }
  return cells
}

/**
 * Romanise one ayah from its tajweed runs.
 *
 * Returns the empty string when there are no runs. In this corpus that never happens —
 * the ayah-level runs are complete, all 6,236 — but the guard means a future change of
 * source produces nothing rather than something wrong.
 */
export function romaniseAyah(runs: readonly TajweedRun[] | undefined): string {
  if (!runs || runs.length === 0) return ''
  return romaniseCells(cellsFromRuns(runs))
}

function romaniseCells(cells: Cell[]): string {
  /** A list, so a written long vowel can lengthen the vowel already emitted. */
  const out: string[] = []
  /** Nothing emitted yet: an alif wasla here is VOICED, not elided. */
  let started = false
  /** The previous letter was an assimilating lām, so the next double takes a hyphen. */
  let assimilating = false
  /**
   * The character just consumed was an alif wasla.
   *
   * This is what tells the definite article's lām from any other vowelless lām. Without
   * it, `قُلْ` and `قَلْبُهُۥ` came out `qul-` and `qal-buhū`: every sukun-bearing lām was
   * being treated as an article.
   */
  let afterWasla = false

  let i = 0
  while (i < cells.length) {
    const cell = cells[i]!
    const ch = cell.ch

    if (ch === ' ' || ch === '\n') {
      if (out.length > 0 && last(out) !== ' ' && last(out) !== '-') out.push(' ')
      i += 1
      continue
    }

    if (isDigit(ch) || (isMark(ch) && ch !== DAGGER_ALIF)) {
      i += 1
      continue
    }

    if (!(ch in CONSONANTS) && !LONG_CARRIERS.has(ch)) {
      i += 1
      continue
    }

    // --- the name of Allah, the one word whose long ā is not written -----------
    const allah = matchAllah(cells, i)
    if (allah !== null) {
      // `prefix` is the first lām's own vowel when it has one: لِلَّهِ is the preposition
      // `li` + the name, and swallowing it turned `lillāhi` into `llāhi`.
      if (allah.prefix !== null) out.push(`l${allah.prefix}`)
      else if (!started) out.push('a')
      out.push('llāh')
      started = true
      i = allah.next
      if (allah.vowel !== null) out.push(allah.vowel)
      assimilating = false
      afterWasla = false
      continue
    }

    // --- gather this letter's marks -------------------------------------------
    i += 1
    let shadda = false
    let vowel: string | null = null
    let tanwin: string | null = null
    let dagger = false
    let smallVowel: string | null = null
    let sukun = false

    while (i < cells.length) {
      const mark = cells[i]!.ch
      if (!isMark(mark)) break
      if (mark === SHADDA) shadda = true
      else if (mark in SHORT_VOWELS) vowel = SHORT_VOWELS[mark]!
      else if (mark in TANWIN) tanwin = TANWIN[mark]!
      else if (mark === SUKUN) sukun = true
      else if (mark === DAGGER_ALIF) dagger = true
      else if (mark === SMALL_WAW) smallVowel = 'ū'
      else if (mark === SMALL_YA) smallVowel = 'ī'
      // The waqf signs add nothing to the output; they are consumed here so the
      // "bare carrier" test below can tell a long vowel from a vowelless consonant.
      i += 1
    }

    /** `slnt`: written, not pronounced. */
    if (cell.rules.includes('slnt')) continue

    const bare = vowel === null && tanwin === null && !dagger && !sukun

    // --- written long vowels ---------------------------------------------------
    /**
     * A SUKUN ON A CARRIER MAKES IT A CONSONANT. `شَىْءٍ` is `shayʾin`, not `shāʾin`:
     * the alif maqsura there carries a sukun and is a yāʾ. The first draft lengthened
     * every carrier unconditionally and lost the glide.
     */
    if (LONG_CARRIERS.has(ch) && ch !== ALIF_WASLA && ch !== DAGGER_ALIF && sukun) {
      out.push('y')
      started = true
      afterWasla = false
      continue
    }
    if (LONG_CARRIERS.has(ch) && ch !== ALIF_WASLA) {
      // After a tanwin the alif is only the tanwin's seat — ًا is `an`, not `anā`.
      if (TANWIN_OUT.has(last(out) ?? '')) continue
      lengthen(out)
      afterWasla = false
      continue
    }
    if (bare && ch === WAW && last(out) === 'u') {
      lengthen(out)
      afterWasla = false
      continue
    }
    if (bare && ch === YA && last(out) === 'i') {
      lengthen(out)
      afterWasla = false
      continue
    }

    /**
     * ALIF WASLA — the prosthetic alif.
     *
     * Voiced only at the START of the utterance (`al-ḥamdu`); everywhere else it elides
     * into the preceding vowel, which is what makes `rabbi l-ʿālamīn` rather than
     * `rabbi al-ʿālamīn`. Its own quality is not written, so it is read off what follows:
     * before a lām — the definite article — it is `a`.
     */
    if (ch === ALIF_WASLA || cell.rules.includes('ham_wasl')) {
      if (!started) {
        out.push(nextLetter(cells, i) === LAM ? 'a' : 'i')
        started = true
      }
      afterWasla = true
      continue
    }

    /**
     * THE ASSIMILATING LĀM, straight from `laam_shamsiyah`.
     *
     * It contributes no `l`; it makes the following consonant double. The hyphen is what
     * keeps the result readable — `ar-raḥmān`, not `arraḥmān`.
     */
    if (ch === LAM && cell.rules.includes('laam_shamsiyah')) {
      assimilating = true
      afterWasla = false
      continue
    }

    let letter = CONSONANTS[ch]!
    if (ch === TA_MARBUTA) letter = vowel !== null || tanwin !== null ? 't' : 'h'

    if (letter === '' && !dagger && smallVowel === null) continue

    /**
     * The definite article's MOON form: a lām with sukun directly after the wasla, as in
     * `ٱلْمُسْتَقِيم`. The `l-` keeps the article visible as an article.
     *
     * `afterWasla` is load-bearing — every other vowelless lām (`قُلْ`, `قَلْبُهُۥ`,
     * `فَلْيُؤَدِّ`) must NOT get the hyphen.
     */
    const moonArticle = ch === LAM && sukun && afterWasla

    if (shadda) letter = assimilating ? hyphenate(letter) : double(letter)
    assimilating = false
    afterWasla = false

    out.push(letter)
    if (letter !== '') started = true
    if (moonArticle) out.push('-')

    if (dagger) out.push('ā')
    else if (smallVowel !== null) out.push(smallVowel)
    else if (tanwin !== null) out.push(tanwin)
    else if (vowel !== null) out.push(vowel)
  }

  return out
    .join('')
    .replace(/ {2,}/gu, ' ')
    .trim()
}

const last = (out: string[]) => out[out.length - 1]

/** `b` → `bb`; `sh` → `shsh` would be unreadable, so a digraph doubles as `sh-sh`. */
const double = (letter: string) => (letter.length <= 1 ? letter + letter : `${letter}-${letter}`)

/** The assimilated article: always hyphenated, so `ar-raḥmān` rather than `arraḥmān`. */
const hyphenate = (letter: string) => `${letter}-${letter}`

/** Lengthen the short vowel just emitted; push the long vowel if there is none. */
function lengthen(out: string[]) {
  const tail = last(out)
  if (tail !== undefined && tail in LENGTHEN) out[out.length - 1] = LENGTHEN[tail]!
  else if (tail !== 'ā' && tail !== 'ī' && tail !== 'ū') out.push('ā')
}

function nextLetter(cells: Cell[], from: number): string | null {
  for (let i = from; i < cells.length; i += 1) {
    const ch = cells[i]!.ch
    if (ch === ' ') return null
    if (!isMark(ch)) return ch
  }
  return null
}

/**
 * `ٱللَّه` — alif wasla, lām, lām + shadda, hā.
 *
 * The long ā between the lām and the hā is NOT WRITTEN, so no per-letter rule can produce
 * it. This is the only word in the text that needs to be recognised as a word, and
 * leaving it out is immediately visible: the first three words of the book come out
 * `bismi llahi` instead of `bismi llāhi`.
 */
function matchAllah(
  cells: Cell[],
  at: number,
): { next: number; vowel: string | null; prefix: string | null } | null {
  let i = at
  if (cells[i]?.ch === ALIF_WASLA) i += 1
  if (cells[i]?.ch !== LAM) return null
  i += 1
  /**
   * The first lām's own vowel, if it has one. `لِلَّهِ` is the preposition `li` plus the
   * name; `ٱللَّهِ` has a bare lām and no prefix.
   */
  let prefix: string | null = null
  while (isMark(cells[i]?.ch ?? '')) {
    const mark = cells[i]!.ch
    if (mark in SHORT_VOWELS) prefix = SHORT_VOWELS[mark]!
    i += 1
  }
  if (cells[i]?.ch !== LAM) return null
  i += 1
  let sawShadda = false
  while (isMark(cells[i]?.ch ?? '')) {
    if (cells[i]!.ch === SHADDA) sawShadda = true
    i += 1
  }
  if (!sawShadda) return null
  if (cells[i]?.ch !== HA) return null
  i += 1
  let vowel: string | null = null
  while (isMark(cells[i]?.ch ?? '')) {
    const mark = cells[i]!.ch
    if (mark in SHORT_VOWELS) vowel = SHORT_VOWELS[mark]!
    else if (mark in TANWIN) vowel = TANWIN[mark]!
    else if (mark === SMALL_WAW) vowel = 'ū'
    i += 1
  }
  return { next: i, vowel, prefix }
}
